import type * as React from "react";
import {
  Button,
  Heading,
  OverlayArrow,
  Text,
  Tooltip as TooltipAria,
  type TooltipProps as TooltipAriaProps,
  TooltipTrigger,
} from "react-aria-components";
import { icon } from "../icon.css.ts";
import { HelpCircle } from "../icons/line.tsx";
import { HelpCircle as HelpCircleSolid } from "../icons/solid.tsx";
import * as styles from "./Tooltip.css.ts";
import type { OmittedReactAriaProps } from "./types.ts";

export interface Props extends Omit<
  TooltipAriaProps,
  OmittedReactAriaProps | "UNSTABLE_portalContainer"
> {
  /** Visual theme of the tooltip. */
  theme?: keyof typeof styles.tooltip.classNames.variants.theme;
  /** Main tooltip text content. */
  text: string;
  /** Additional supporting text displayed below the main text. */
  supportingText?: string;
  /** The trigger element that shows the tooltip on hover/focus. */
  children: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * A tooltip component that displays contextual information on hover or focus.
 * Supports light and dark themes with optional supporting text.
 */
export const Tooltip: React.FC<Props> = ({
  text,
  supportingText,
  children,
  theme = "dark",
  ref,
  ...props
}) => {
  const { isOpen, ...tooltipProps } = props;

  return (
    <TooltipTrigger isOpen={isOpen} delay={0}>
      {children}
      <TooltipAria
        offset={styles.tooltipGap + styles.overlayArrowHeight}
        className={styles.tooltip({ theme })}
        {...tooltipProps}
        ref={ref}
      >
        <OverlayArrow className={styles.overlayArrow({ theme })}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="17"
            height="9"
            viewBox="0 0 17 9"
            className={styles.overlayArrowSvg}
          >
            <path d="M14.5711.485289c.8909 0 1.337 1.077141.7071 1.707111L9.20711 8.26347c-.39053.39053-1.02369.39053-1.41422 0L1.72183 2.1924c-.62996-.62997-.1838-1.707111.70711-1.707111H14.5711Z" />
          </svg>
        </OverlayArrow>
        {supportingText == null ? (
          <Text slot="description" className={styles.tooltipTextHeader}>
            {text}
          </Text>
        ) : (
          <>
            <Heading slot="header" className={styles.tooltipTextHeader}>
              {text}
            </Heading>
            <Text slot="description" className={styles.tooltipTextSupporting}>
              {supportingText}
            </Text>
          </>
        )}
      </TooltipAria>
    </TooltipTrigger>
  );
};

type IconColor = keyof typeof styles.helpIcon.classNames.variants.color;
export type IconSize = keyof typeof styles.helpIcon.classNames.variants.size;

interface IconProps {
  /** Color variant of the help icon. */
  color?: IconColor;
  /** Size of the help icon. */
  size?: IconSize;
  /** When true, shows a solid icon on focus instead of outline. */
  solidOnFocus?: boolean;
}

export const TooltipIcon: React.FC<IconProps> = ({
  color,
  size = "s",
  solidOnFocus = false,
}) => {
  return (
    <Button className={styles.helpIcon({ size, color })}>
      {solidOnFocus ? (
        ({ isFocusVisible }) =>
          isFocusVisible ? (
            <HelpCircleSolid className={icon({ size })} />
          ) : (
            <HelpCircle className={icon({ size })} />
          )
      ) : (
        <HelpCircle className={icon({ size })} />
      )}
    </Button>
  );
};
